import React, { createContext, useContext, useState, useEffect } from "react";
import api from "@/services/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        // Check if this is an embajador session (they have their own auth system)
        const embajador = localStorage.getItem("embajador");
        if (embajador) {
          // Embajador users don't use /auth/me, they have their own session in localStorage
          // Skip validation here - embajador routes handle their own auth
          setLoading(false);
          return;
        }
        
        try {
          const response = await api.get("/auth/me");
          setUser(response.data);
        } catch (error) {
          console.error("Error validating token:", error);
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { token: newToken, usuario, embajador } = response.data;
    
    localStorage.setItem("token", newToken);
    if (usuario?.perfil_comercial === "embajador_digital" && embajador) {
      // Vendedor digital migrado: habilitar experiencia de embajador
      localStorage.setItem("embajador", JSON.stringify(embajador));
      localStorage.setItem("userRole", "embajador");
    }
    setToken(newToken);
    setUser(usuario);
    
    return { usuario, embajador };
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("embajador"); // Also clear embajador session
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.rol === "admin" || user?.rol === "supervisor",
    isVendedor: user?.rol === "vendedor",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
